<template>
    <div>
        <report-header
            :title="title"
            :isShowDateRange="true"
            :formatDateRangeTitle="dateRange"
        />
        <b-card id="report-card">
            <loader-barchart
                v-if="isLoading"
                class="graph-overlay"
                style="height: 350px; margin: 2em 0px 2em 20px;"
                :styles="'height: 350px'"
            />
            <div
                id="monthly-billing-summary-chart"
                style="height: 350px; margin: 2em 0px 2em 20px;"
            ></div>
            <div id="report-body" class="w-100">
                <QTable
                    :data="results"
                    :columns="headers"
                    :hide-bottom="true"
                    :rows-per-page-options="[0]"
                    row-key="item_id"
                    :loading="isLoading"
                    class="q-table"
                >
                    <template v-slot:bottom-row>
                        <q-tr
                            v-if="results.length == 0 && !isLoading && !isError"
                        >
                            <q-td colspan="6">No records available</q-td>
                        </q-tr>
                    </template>
                    <template v-slot:header-cell="props">
                        <q-th :props="props">
                            <strong>{{ props.col.label }}</strong>
                        </q-th>
                    </template>
                    <template v-slot:body-cell="{ row, value, col }">
                        <q-td :class="col.__tdClass" :style="col.style">
                            <strong
                                v-if="row.type == 'Tax' || row.type == 'Total'"
                            >
                                <span v-if="col.label == 'Type'">{{
                                    row.type
                                }}</span>
                                <span v-else>{{ value }}</span>
                            </strong>
                            <span v-else-if="col.label == 'Type'">{{
                                row.type
                            }}</span>
                            <span v-else>{{ value }}</span>
                        </q-td>
                    </template>
                </QTable>
            </div>
        </b-card>
    </div>
</template>
<script>
import { QTable } from "quasar";
import "@/jquery/jquery.flot.tooltip";
import "@/css/quasar-table.css";
import Report from "@/mixins/report.js";
import ReportService from "@/services/report";
import CREPORT from "@/constants/report";
import { forEach } from "lodash";

export default {
    mixins: [Report],
    components: {
        QTable,
        LoaderBarchart: () => import("@/components/elements/loader-barchart"),
        ReportHeader: () => import("@/components/report/report-header")
    },
    props: {
        data: {
            type: Object,
            default() {
                return {
                    start: this.getInitalMonthChart(),
                    end: this.getEndDate("month"),
                    datePickerOptions: {
                        format: this.datepickerMonthFormat
                    }
                };
            }
        }
    },
    data() {
        return {
            isLoading: true,
            isError: false,
            dateRange: "",
            title: "Monthly Billing Summary",
            headers: [],
            results: [],
            dataGraph: {},
            options: {}
        };
    },
    async mounted() {
        let self = this;
        self.data.isShowDateRange = true;
        self.data.datePickerOptions.format = self.datepickerMonthFormat;
        self.data.isFilterDateMonthOnly = true;
        self.data.datePickerOptionDefault = 1;

        self._getSetting().then(() => {
            self.data.start = self.getInitalMonthChart();
            self.data.end = self.getEndDate("month");
            self.data.from = self.converToUnix(self.data.start);
            self.data.to = self.converToUnix(self.data.end);
            self.init();
        });

        self.$root.$on("report-filter-date-range:refresh", this.init);
        window.scrollTo(0, 0);
    },
    beforeDestroy: function () {
        this.data.datePickerOptions.format = this.getDateFormat();
        this.data.isFilterDateMonthOnly = false;
        this.data.datePickerOptionDefault = null;
        this.$root.$off("report-filter-date-range:refresh", this.init);
    },
    methods: {
        endDate() {
            return moment().tz(this._timeZone);
        },
        async init(params) {
            let self = this;
            self.isLoading = true;
            self.isError = false;

            if (params) {
                self.data.from = params.from;
                self.data.to = params.to;
            }
            self.dateRange = self.getDateRange(
                self.data.from,
                self.data.to,
                self.formatInMonth
            );
            await ReportService.getReport(
                CREPORT.TYPE.MONTHLY_BILLING_SUMMARY,
                {
                    from: self.data.from,
                    to: self.data.to
                }
            )
                .then(res => {
                    if (res && res.message && res.message.code === 200) {
                        self.headers = self.reanderHeaders(
                            res.resource.results.header_summary
                        );
                        self.results = self.renderResults(
                            res.resource.results.result_summary
                        );
                        self.dynamicPageTitle(self.title);
                        self.dateRange = self.getDateRange(
                            res.resource.start_date,
                            res.resource.end_date,
                            self.formatInMonth
                        );
                        self.renderGraph(res.resource.results.result_summary);
                    } else {
                        throw new Error("Error: Unable to get the report.");
                    }
                })
                .catch(e => {
                    console.error(e);
                    self.isLoading = false;
                    self.isError = true;
                    // DEFAULT CHART AND TABLE WHEN SOMETHING WRONG
                    self.headers = [
                        {
                            name: "type",
                            label: "Type",
                            field: "type",
                            align: "left",
                            style: ""
                        },
                        {
                            name: "month",
                            label: "Month",
                            field: "month",
                            align: "left",
                            style: ""
                        }
                    ];
                    self.renderGraph({ Total: { Month: "0" } });
                    self._message("Error: Unable to get the report", "danger");
                })
                .finally(() => {
                    self.isLoading = false;
                });
        },
        renderGraph(results) {
            let xaxis = [];
            let xaxisLabel = [];
            let index = 1;
            let self = this;
            forEach(results.Total, (total, month) => {
                if (total !== "Total") {
                    xaxis.push([
                        index,
                        total.replace(this._currency, "").replaceAll(",", "")
                    ]);
                    xaxisLabel.push([index, month]);
                    ++index;
                }
            });

            self.dataGraph = [
                {
                    label: "",
                    data: xaxis,
                    color: "#84BBF9"
                }
            ];

            self.options = {
                series: {
                    bars: {
                        show: true,
                        barWidth: 0.9,
                        align: "center",
                        lineWidth: 0,
                        fillColor: {
                            colors: [{ opacity: 0.8 }, { opacity: 0.8 }]
                        }
                    },
                    points: {
                        show: false
                    }
                },
                grid: {
                    borderWidth: 0,
                    hoverable: true
                },
                xaxis: {
                    ticks: xaxisLabel
                },
                yaxis: {
                    axisLabel: "",
                    axisLabelUseCanvas: false,
                    tickDecimals: 0,
                    tickFormatter: function numberWithCommas(x) {
                        return self._toCurrency(x, false);
                    }
                },
                tooltip: true,
                tooltipOpts: {
                    content: function (label, xval, yval, flotItem) {
                        return (
                            self.options.xaxis.ticks[xval - 1][1] +
                            " (" +
                            self._toCurrency(yval) +
                            ")"
                        );
                    },
                    shifts: {
                        x: 20,
                        y: 0
                    },
                    defaultTheme: false
                }
            };

            $.plot(
                $("#monthly-billing-summary-chart"),
                self.dataGraph,
                self.options
            );
            $(window).resize(function () {
                if (
                    self.$router.history.current.path ==
                    "/report/" + CREPORT.TYPE.MONTHLY_BILLING_SUMMARY
                ) {
                    $.plot(
                        $("#monthly-billing-summary-chart"),
                        self.dataGraph,
                        self.options
                    );
                }
            });
        },
        reanderHeaders(dataHeaders) {
            let headers = [];
            forEach(dataHeaders, function (header) {
                headers.push({
                    name: header == "type" ? header.toLowerCase() : header,
                    label: header,
                    field: header == "type" ? header.toLowerCase() : header,
                    align: "left",
                    style: ""
                });
            });
            return headers;
        },
        renderResults(data) {
            let results = [];
            forEach(data, function (result) {
                results.push(result);
            });
            return results;
        }
    }
};
</script>
<style>
.graph-overlay {
    position: absolute;
    display: block;
    top: 0;
    left: 0;
    right: 0;
    background-color: #fff;
    z-index: 2;
    cursor: pointer;
}
</style>
